import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo } from "react";
import { ImpactCardGrid } from "@/components/impactReport/ImpactCardGrid";
import {
  ImpactEmptyState,
  resolveImpactEmptyStateKind,
} from "@/components/impactReport/ImpactEmptyState";
import { ImpactHeroDocument } from "@/components/impactReport/ImpactHeroDocument";
import { ImpactReportColophon } from "@/components/impactReport/ImpactReportColophon";
import { ImpactReportDeferredState } from "@/components/impactReport/ImpactReportDeferredState";
import { ImpactReportMasthead } from "@/components/impactReport/ImpactReportMasthead";
import {
  isNoCampaignsInScopeError,
  resolveImpactReportErrorMessage,
} from "@/components/impactReport/impact-report-error";
import {
  buildImpactReportCappedSelectionNotice,
  buildImpactReportSelectionEmptyMessage,
  IMPACT_REPORT_CAPPED_SELECTION_TITLE,
  IMPACT_REPORT_DEFAULT_DATE_PRESET,
  IMPACT_REPORT_EMPTY_SELECTION_ACTION_LABELS,
  IMPACT_REPORT_ERROR_STATE_FALLBACK_BODY,
  IMPACT_REPORT_ERROR_STATE_TITLE,
  IMPACT_REPORT_JOB_FAILED_ACTION_LABEL,
  IMPACT_REPORT_JOB_FAILED_BODY,
  IMPACT_REPORT_JOB_FAILED_TITLE,
  IMPACT_REPORT_JOB_GONE_ACTION_LABEL,
  IMPACT_REPORT_JOB_GONE_BODY,
  IMPACT_REPORT_JOB_GONE_TITLE,
  IMPACT_REPORT_UNAVAILABLE_STATE_BODY,
  IMPACT_REPORT_UNAVAILABLE_STATE_TITLE,
} from "@/constants/impactReport";
import { useImpactReportContext } from "@/context/ImpactReportContext";
import { useCampaignOptionsByIds } from "@/hooks/useCampaignOptions";
import {
  useDiscardImpactReport,
  useImpactReport,
  useImpactReportJob,
} from "@/hooks/useImpactReport";
import { usePermissions } from "@/hooks/usePermissions";
import type {
  CappedImpactCampaignSelection,
  ImpactEmptySelectionAction,
  ImpactEmptyStateProps,
} from "@/interfaces/impactReport";
import {
  describeImpactDatePhrase,
  findSingleSelectedCampaign,
  resolveImpactEmptySelectionAction,
} from "@/utils/impact-report-filter-helper";
import {
  buildImpactReportPageModel,
  describeImpactReportError,
  isClientImpactReportDeferred,
  isImpactReportJobNotFoundError,
  isImpactReportUnavailableError,
  resolveImpactReportPhotoCredits,
} from "@/utils/impact-report-helper";

/**
 * `/impact` — the client impact report. Owns the query and the page states; the masthead, the
 * paginated hero document, the colophon, and the "Explore your Impact" card grid compose beneath
 * it. The control bar that decides WHAT is drawn lives in `Drawer.tsx`'s app bar, above this
 * page's own outlet, and the two share `ImpactReportContext`.
 *
 * The filter is composed from the user's selections by task 1's helper (never inline, here or
 * anywhere). An organization user's empty selection is their whole organization, so their page
 * opens on the all-time, all-campaigns report. GFG staff have no single organization, so for them
 * the page is SELECTION-FIRST: the query stays DISABLED until a campaign is chosen, because the
 * endpoint refuses their unscoped request.
 *
 * ONE component, TWO render branches. The view mode chooses between them and nothing else: it
 * reaches no query key, no hook argument and no invalidation, and because both branches live in
 * this one component, switching them never remounts the page onto a cold cache. Both statement
 * registers ship on every metric precisely so that switch is free.
 *
 * Consumes slice 1's `useImpactReport` ONCE — `ImpactCardGrid` below is handed this same call's
 * state rather than calling the hook itself.
 */
export default function ImpactReport() {
  const {
    controls,
    filter,
    viewMode,
    canRequestReport,
    setControls,
    campaignSelection,
    hasCauseSelection,
    causeOptions,
    refreshCauseOptions,
    openCampaignMenu,
    campaignMenuTriggerRef,
    causeMenuTriggerRef,
    dateMenuTriggerRef,
  } = useImpactReportContext();

  // The composed filter and the gate, both from context: the control bar passes the SAME two
  // values to its own `useImpactReport` call, so react-query hashes one key and the two calls
  // share one request.
  const {
    data: response,
    error,
    isLoading,
    isError,
    isRefetching,
    refetch,
    isAwaitingUser,
  } = useImpactReport(filter, canRequestReport);

  // The context withholds the org-wide scope until permissions say who the caller is, so until
  // then `canRequestReport` is false for an organization user too. That is a wait, not the
  // pre-selection state: without this an organization user would flash "Choose a campaign" on
  // every cold load. A permissions failure ends the wait and falls back to selection-first.
  const { isLoaded: arePermissionsLoaded, isError: arePermissionsErrored } = usePermissions();
  const isAwaitingPermissions = !arePermissionsLoaded && !arePermissionsErrored;

  // A deferred answer carries no report: it is built in a background job, polled here until it
  // completes. The SAME `report` then feeds every renderer below, whichever way it arrived.
  const deferral = isClientImpactReportDeferred(response) ? response : null;
  const {
    data: job,
    error: jobError,
    isError: isJobError,
    isRefetching: isJobRefetching,
    refetch: refetchJob,
  } = useImpactReportJob(deferral ? deferral.jobId : null);
  const completedJobReport = job?.status === "completed" ? job.report : undefined;
  const report = isClientImpactReportDeferred(response) ? completedJobReport : response;
  const discardImpactReport = useDiscardImpactReport();

  // The control bar only ever sees the deferral, so the finished report's causes reach the CAUSE
  // menu from here.
  useEffect(() => {
    if (!completedJobReport) return;
    refreshCauseOptions(completedJobReport.data.causeData);
  }, [completedJobReport, refreshCauseOptions]);

  // Only a single selection is ever named, in the selection-empty message below. Same ids as the
  // filter pills' call, so react-query hashes one key and the two share one request.
  const { data: selectedCampaignOptions } = useCampaignOptionsByIds(
    controls.campaignIds.length === 1 ? [...controls.campaignIds] : [],
  );

  // The error state's copy is deliberately generic, so the console is the ONLY place the
  // discriminating detail (`DashboardReportErrorCode`, HTTP status) survives — and a failure here
  // is final (`retry: false`), so it must be legible in the field. Keyed on `error`, so one
  // failed fetch logs exactly once rather than once per render.
  useEffect(() => {
    if (!error) return;
    console.error(
      "ImpactReport: client impact report request failed",
      describeImpactReportError(error),
    );
  }, [error]);
  useEffect(() => {
    if (!jobError) return;
    console.error(
      "ImpactReport: deferred report job request failed",
      describeImpactReportError(jobError),
    );
  }, [jobError]);

  // The page model is built ONCE, here — the hero document reads it when there's something to
  // paginate. Emptiness itself, though, is decided below off `causeData` directly rather than off
  // `pages.length`, because that same decision has to answer for the card branch too, and the
  // grid never builds a page model at all.
  //
  // `hasCauseSelection` is THE suppression decision, and this page is the only place it is read:
  // the pages below and the photo credits further down are two derivations of this one boolean, so
  // the document and the credits that describe it cannot disagree about whether the cover exists.
  // It is passed INTO the builder rather than applied to what the builder returns, because that
  // array is the whole contract with every consumer — the PDF export stage re-renders exactly it.
  const pages = useMemo(
    () => (report ? buildImpactReportPageModel(report, hasCauseSelection) : []),
    [report, hasCauseSelection],
  );

  // Distinct from a transport failure: the request resolved but returned no payload, which is a
  // contract violation on the API side, not a network problem. Only meaningful once a request has
  // actually been made — a DISABLED query has no payload by design, and reporting that as a
  // contract violation would log an error on every page load. `!isAwaitingUser` for the same
  // reason: the query is also disabled (idle, not loading) during the brief window before Clerk
  // resolves, and that is not a contract violation either.
  const isMissingPayload =
    canRequestReport && !isAwaitingUser && !isLoading && !isError && !response;
  useEffect(() => {
    if (!isMissingPayload) return;
    console.error(
      "ImpactReport: client impact report resolved with no payload (GET /reports/client-impact-report)",
    );
  }, [isMissingPayload]);

  // CHECKED BEFORE THE PRE-SELECTION STATE BELOW. `useImpactReport` disables its query until
  // Clerk's `useUser().isLoaded` resolves (see that hook), and a disabled query is idle, not
  // loading — so without this branch, the brief window before Clerk resolves would fall through
  // to the pre-selection prompt (or, once a selection exists, the error state) rather than a
  // loading state, on every mount. Same loading UI as `isLoading` below on purpose: neither tells
  // the reader WHY it's waiting, only that it is.
  // `isAwaitingPermissions` is the same window for the caller's permissions, gated on
  // `!canRequestReport` so a chosen campaign never waits on them.
  if (isAwaitingUser || (!canRequestReport && isAwaitingPermissions)) {
    return <ImpactReportLoadingState />;
  }

  // THE PRE-SELECTION STATE, and it is checked FIRST *of the remaining branches*. Only GFG staff
  // with no campaign chosen reach it: an organization user's empty selection is the org-wide
  // report, which is always requestable. A disabled query is idle, not loading, so without this
  // branch the page would fall through to the error state on mount. It is emphatically NOT the
  // selection-empty state: nothing has been asked yet, so nothing has come back empty, and telling a client their selection has no impact when they
  // have merely not chosen one is a wrong answer rather than an empty one.
  if (!canRequestReport) {
    return <ImpactEmptyState kind="preSelection" />;
  }

  if (isLoading) {
    return <ImpactReportLoadingState />;
  }

  // A deferred report is never an empty state or an error while its job is still running — and
  // before the job's first fetch lands, it is still running.
  if (deferral && !isError) {
    if (isJobError) {
      // The job Crunchy was building for THIS deferral has expired or been lost
      // (`REPORT_JOB_NOT_FOUND`) — a dead end `refetchJob` cannot recover from, since polling the
      // same jobId only 404s again. Distinct from every other job error below: the deferral is
      // forgotten (query cache AND its sessionStorage entry) and a fresh report is requested for
      // the SAME selection, rather than offering a retry that can never succeed.
      if (isImpactReportJobNotFoundError(jobError)) {
        return (
          <ImpactReportJobGoneState
            onBuildAgain={() => {
              // Focus first, for the same reason as the empty state's actions below: discarding
              // and refetching unmounts this button.
              campaignMenuTriggerRef.current?.focus();
              // `queryClient.removeQueries()` on its own does NOT re-fetch an active query —
              // verified against `@tanstack/query-core`: removing a query with a live observer
              // fires no new network call and no observer notification by itself. `discardImpactReport`
              // still has to run first (it also clears the SAME user's sessionStorage entry, see
              // `utils/impact-report-helper.ts`), but reaching the network again needs an explicit
              // `refetch()` on `useImpactReport`'s own query, same selection, right after. Together
              // that is exactly ONE `GET /reports/client-impact-report`, fired only by this click:
              // the stored deferral is gone, so `queryFn` falls through to a real fetch instead of
              // replaying it.
              discardImpactReport(filter);
              void refetch();
            }}
          />
        );
      }
      return (
        <ImpactReportErrorState
          onRetry={() => {
            // Focus first, as in `retryReport` below: a successful retry unmounts this button.
            campaignMenuTriggerRef.current?.focus();
            void refetchJob();
          }}
          isRetrying={isJobRefetching}
          error={jobError}
        />
      );
    }
    if (job?.status === "failed") {
      return (
        <ImpactReportJobFailedState
          onStartOver={() => {
            // Focus first, for the same reason as the empty state's actions below: clearing the
            // campaigns unmounts this button.
            campaignMenuTriggerRef.current?.focus();
            // The failed deferral is never stale; forget it so re-selecting the same campaigns
            // starts a new job instead of landing straight back here.
            discardImpactReport(filter);
            setControls((previous) => ({ ...previous, campaignIds: [] }));
          }}
        />
      );
    }
    if (job?.status !== "completed") {
      return <ImpactReportDeferredState recipient={deferral.recipient} />;
    }
  }

  // `useImpactReport` sets `retry: false` deliberately (see the hook) — a failed fetch is FINAL
  // until the user asks again, so this manual retry is the only recovery path. Never render a
  // partial document here: a half-drawn client report reads as a broken report. An error is
  // never one of the empty states — see `ImpactReportErrorState`.
  // A group with no campaigns yet is answered, not failed: an empty state, with no Retry to offer.
  if (isError && isNoCampaignsInScopeError(error)) {
    return <ImpactEmptyState kind="noCampaignsInScope" />;
  }

  if (isError || !report) {
    // Focus first, for the same reason as the empty state's actions below. The query stays in
    // its error state while the refetch is in flight, so this button stays mounted (disabled, as
    // "Retrying…") until it lands — but a SUCCESS swaps the whole error subtree for the report and
    // would drop focus to `<body>`. The campaign trigger lives in the app bar and survives that.
    const retryReport = () => {
      campaignMenuTriggerRef.current?.focus();
      void refetch();
    };
    return <ImpactReportErrorState onRetry={retryReport} isRetrying={isRefetching} error={error} />;
  }

  const { dateRange, thankYou } = report.data;

  // ONE condition, decided ONCE, shared by both branches below — the two must never be free to
  // disagree about whether the payload is empty. Non-null here IS "empty"; there's no separate
  // boolean to keep in sync with it.
  const emptyStateKind =
    report.data.causeData.length === 0 ? resolveImpactEmptyStateKind(report.data) : null;

  // Built once and handed to both branches, as a discriminated union so a `noDedicatedPage` render
  // can never carry `selectionEmpty`'s button by accident, and a `selectionEmpty` render can never
  // omit it — see `ImpactEmptyStateProps`. Every part of the message describes what was ASKED: the
  // date phrase reads `controls.date`, never `dateRange`, which is null on exactly this empty
  // payload. The count is the user's own selection even when capped — the cap notice above the
  // report already qualifies it.
  let emptyStateProps: ImpactEmptyStateProps | null = null;
  if (emptyStateKind === "selectionEmpty") {
    const action = resolveImpactEmptySelectionAction(controls);
    // Focus moves to the app bar's own filter trigger BEFORE `setControls` fires. `setControls`
    // changes `filter`'s react-query key, so `isLoading` flips true and this page swaps its WHOLE
    // body — this button included — for `ImpactReportLoadingState` in the same commit; focusing
    // afterwards would reach for a node that has already unmounted and drop focus to `<body>` with
    // no announcement. `ImpactControlBar` (and so its DATE/CAUSE triggers) renders in `Drawer.tsx`'s
    // app bar, above the outlet, so it survives that swap untouched — see `causeMenuTriggerRef` /
    // `dateMenuTriggerRef` on the context. `.focus()`, never `.click()`: landing focus must not also
    // pop the menu open. `showCampaigns` needs none of this: `openCampaignMenu` never touches
    // `controls`, so the button it clicks was never at risk of unmounting.
    const onAction: Record<ImpactEmptySelectionAction, () => void> = {
      widenDate: () => {
        dateMenuTriggerRef.current?.focus();
        setControls((previous) => ({
          ...previous,
          date: { preset: IMPACT_REPORT_DEFAULT_DATE_PRESET },
        }));
      },
      clearCause: () => {
        causeMenuTriggerRef.current?.focus();
        setControls((previous) => ({ ...previous, cause: [] }));
      },
      showCampaigns: openCampaignMenu,
    };
    emptyStateProps = {
      kind: "selectionEmpty",
      selectionEmpty: {
        message: buildImpactReportSelectionEmptyMessage({
          campaignCount: campaignSelection.selectedCount,
          singleCampaignName:
            findSingleSelectedCampaign(controls.campaignIds, selectedCampaignOptions)?.name ?? null,
          causeNames: controls.cause.map(
            (causeValue) =>
              causeOptions.find((option) => option.causeValue === causeValue)?.causeName ??
              causeValue,
          ),
          datePhrase: describeImpactDatePhrase(controls.date, new Date()),
        }),
        actionLabel: IMPACT_REPORT_EMPTY_SELECTION_ACTION_LABELS[action],
        onAction: onAction[action],
      },
    };
  } else if (emptyStateKind === "noDedicatedPage") {
    emptyStateProps = { kind: "noDedicatedPage" };
  }

  return (
    <Box className="irv-report">
      {/* The cap, said out loud. Above the report, because the thing being qualified is the whole
          document beneath it. */}
      {campaignSelection.wasCapped && <ImpactReportCapNotice selection={campaignSelection} />}

      {/* VIEW chooses the branch — and ONE empty state per page, never two. Both branches below
          intercept the SAME `emptyStateProps`, so a page can never print a document/grid in one
          branch and the empty state in the other. The card grid still carries its OWN, separate
          empty state further down (see `ImpactCardGrid`) for the residual case this page cannot
          see from here — non-empty `causeData` with zero flattened impact types — and that case is
          disjoint from this one by construction: the grid is only ever reached once `causeData` is
          non-empty. */}
      {viewMode === "hero" ? (
        <>
          <ImpactReportMasthead dateRange={dateRange} />
          {emptyStateProps ? (
            <ImpactEmptyState {...emptyStateProps} />
          ) : (
            <>
              <ImpactHeroDocument pages={pages} />
              {/* The SAME suppression decision `pages` was built from. The credits carry Crunchy's
                  page numbers, so a document that dropped its first page has to be described by
                  credits that agree with it — see the resolver for the rule and its limits. */}
              <ImpactReportColophon
                photoCredits={resolveImpactReportPhotoCredits(
                  thankYou?.photoCredits,
                  hasCauseSelection,
                )}
              />
            </>
          )}
        </>
      ) : emptyStateProps ? (
        <ImpactEmptyState {...emptyStateProps} />
      ) : (
        // The grid carries its own heading and its own empty state, so the card branch is the grid
        // alone. `isLoading` / `isError` are already false on this line — they are forwarded
        // rather than hard-coded so the grid keeps rendering the page's real state.
        <ImpactCardGrid report={report} isLoading={isLoading} isError={isError} error={error} />
      )}
    </Box>
  );
}

interface ImpactReportCapNoticeProps {
  selection: CappedImpactCampaignSelection;
}

/**
 * "This report covers 250 of your 312 selected campaigns."
 *
 * Rare — selection-first means nothing is enumerated on mount, so it takes an organization with
 * more than 250 campaigns whose user selects them all — but never silent. A report that quietly
 * answers "your first 250 campaigns" while presenting itself as the client's complete impact is a
 * wrong answer, not a limitation.
 */
function ImpactReportCapNotice({ selection }: ImpactReportCapNoticeProps) {
  return (
    <ImpactReportNotice
      title={IMPACT_REPORT_CAPPED_SELECTION_TITLE}
      body={buildImpactReportCappedSelectionNotice(selection.selectedCount, selection.limit)}
    />
  );
}

interface ImpactReportNoticeProps {
  title: string;
  body: string;
}

/**
 * The one qualification banner the report has — today only the 250-id request cap uses it — so any
 * later "this document covers less than you asked for" case reads in the same visual language.
 */
function ImpactReportNotice({ title, body }: ImpactReportNoticeProps) {
  return (
    <Box
      role="status"
      sx={{
        my: "16px",
        px: "16px",
        py: "12px",
        border: "1px solid var(--color-rodeo-dust)",
        borderRadius: "10px",
        bgcolor: "var(--color-light-dust)",
      }}
    >
      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "var(--color-rodeo-black)" }}>
        {title}
      </Typography>
      <Typography sx={{ mt: "2px", fontSize: "13px", color: "var(--color-graphite)" }}>
        {body}
      </Typography>
    </Box>
  );
}

/**
 * A quiet placeholder, styled like the rest of the dashboard's loading states.
 *
 * `role="status"` + `aria-live="polite"`: this replaces the whole page — including the empty
 * state's action button a `widenDate`/`clearCause` click just focused — so a screen reader user
 * who triggered that swap gets an announcement in its place instead of silence. `showCampaigns`
 * never reaches this state at all (`openCampaignMenu` issues no request).
 */
function ImpactReportLoadingState() {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        gap: 2,
      }}
    >
      <CircularProgress sx={{ color: "var(--color-dark-dust)" }} />
      <Typography sx={{ fontSize: "14px", color: "var(--color-graphite)" }}>
        Loading your impact report…
      </Typography>
    </Box>
  );
}

interface ImpactReportErrorStateProps {
  onRetry: () => void;
  isRetrying: boolean;
  /** The hook's own `error`. Read here, via the shared resolver, for the API's OWN message. */
  error: unknown;
}

/**
 * A short message and nothing else — no masthead, no document, ever, and never one of the empty
 * states: a failed request and an empty result answer two different questions, and telling a
 * client their selection has no impact for a transport failure answers the wrong one.
 *
 * When the reporting service is down or refused Platform (`isImpactReportUnavailableError`), the
 * copy says so and offers no filter advice; every other failure keeps the generic title and the
 * API's own message.
 */
function ImpactReportErrorState({ onRetry, isRetrying, error }: ImpactReportErrorStateProps) {
  const isUnavailable = isImpactReportUnavailableError(error);
  // Only the words are the alert; the button is its sibling, so pressing it does not re-announce.
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "320px",
        gap: 1.5,
      }}
    >
      <Box
        role="alert"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "var(--color-rodeo-black)" }}>
          {isUnavailable ? IMPACT_REPORT_UNAVAILABLE_STATE_TITLE : IMPACT_REPORT_ERROR_STATE_TITLE}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "var(--color-graphite)",
            textAlign: "center",
            maxWidth: "360px",
          }}
        >
          {isUnavailable
            ? IMPACT_REPORT_UNAVAILABLE_STATE_BODY
            : resolveImpactReportErrorMessage(error, IMPACT_REPORT_ERROR_STATE_FALLBACK_BODY)}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        onClick={() => onRetry()}
        disabled={isRetrying}
        sx={{ borderColor: "var(--color-dark-dust)", color: "var(--color-rodeo-black)" }}
      >
        {isRetrying ? "Retrying…" : "Try again"}
      </Button>
    </Box>
  );
}

interface ImpactReportJobFailedStateProps {
  onStartOver: () => void;
}

/**
 * A deferred report whose job failed. Same shape as the error state — the words are the alert,
 * the way back is its sibling. The job's own error text is never shown.
 */
function ImpactReportJobFailedState({ onStartOver }: ImpactReportJobFailedStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "320px",
        gap: 1.5,
      }}
    >
      <Box
        role="alert"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "var(--color-rodeo-black)" }}>
          {IMPACT_REPORT_JOB_FAILED_TITLE}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "var(--color-graphite)",
            textAlign: "center",
            maxWidth: "360px",
          }}
        >
          {IMPACT_REPORT_JOB_FAILED_BODY}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        onClick={() => onStartOver()}
        sx={{ borderColor: "var(--color-dark-dust)", color: "var(--color-rodeo-black)" }}
      >
        {IMPACT_REPORT_JOB_FAILED_ACTION_LABEL}
      </Button>
    </Box>
  );
}

interface ImpactReportJobGoneStateProps {
  onBuildAgain: () => void;
}

/**
 * A deferred report whose job Crunchy expired or lost (`REPORT_JOB_NOT_FOUND`) mid-poll. Same
 * shape as the other terminal states on this page — the words are the alert, the way back is its
 * sibling — but distinct copy and a distinct action from `ImpactReportJobFailedState`: that one
 * clears the campaign selection back to the pre-selection prompt (the job genuinely FAILED and
 * Crunchy will never complete it); this one keeps the SAME selection and re-requests it, since
 * nothing about the selection was wrong — the job simply outlived its own lifetime before the tab
 * came back to it.
 */
function ImpactReportJobGoneState({ onBuildAgain }: ImpactReportJobGoneStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "320px",
        gap: 1.5,
      }}
    >
      <Box
        role="alert"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "var(--color-rodeo-black)" }}>
          {IMPACT_REPORT_JOB_GONE_TITLE}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "var(--color-graphite)",
            textAlign: "center",
            maxWidth: "360px",
          }}
        >
          {IMPACT_REPORT_JOB_GONE_BODY}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        onClick={() => onBuildAgain()}
        sx={{ borderColor: "var(--color-dark-dust)", color: "var(--color-rodeo-black)" }}
      >
        {IMPACT_REPORT_JOB_GONE_ACTION_LABEL}
      </Button>
    </Box>
  );
}
